import EventBus from "../../../../util/EventBus";
import Applet from "../../Applet";
import PlatformDetailDialog from "../../../../components/PlatformDetailDialog";

const PLACEMENT = {
    "View": {xs: 12, sm: 12, md: 12, lg: 12, xl: 12},
    "Edit": {xs: 12, sm: 12, md: 12, lg: 12, xl: 12},
    "New": {xs: 12, sm: 12, md: 12, lg: 12, xl: 12},
};

/**
 * Relate this object to another one.
 *
 * TWO QUESTIONS, IN THE ORDER THEY ARE ANSWERED: what kind of edge, then which object. The type
 * first because it decides how the row will read -- "is a component of X" -- and choosing the
 * object before knowing the phrasing invites picking the wrong end of a directional pair.
 *
 * PARENTUSID IS NOT ASKED FOR. It is the record the dialog was opened from; offering it as a field
 * would invite relating two objects that have nothing to do with the page. The table sets it before
 * the dialog opens.
 *
 * EDIT IS NOT OFFERED EITHER. An edge is a type and two endpoints and nothing else, so "changing"
 * one is removing it and adding the one you meant -- which is also what keeps the audit trail
 * honest about what was linked when.
 */
class ObjectRelationshipFormApplet extends Applet {
    state = {
        Name: "ObjectRelationshipFormApplet",
        Actions: {
            New: {
                Groups: [{
                    "identifier": "group1",
                    "type": "standard",
                    "selector": null,
                    "characteristics": [],
                    "membership": [
                        {id: "ObjectRelationshipTypeUSID"},
                        {id: "ChildName"},
                        {id: "PinToRevision"},
                    ],
                }],
            },
            // DELETE MUST BE DECLARED, EVEN THOUGH ITS FIELDS ARE THE SAME. The dialog reads the
            // action to decide what it is doing; without this entry it fell back to the New layout
            // AND to New's behaviour, so "Remove relationship" opened an Add dialog with a Save
            // button -- which would have written a second edge rather than removing the one chosen.
            // Same membership on purpose: a confirmation should show the row it is about to remove.
            Delete: {
                Groups: [{
                    "identifier": "group1",
                    "type": "standard",
                    "selector": null,
                    "characteristics": [],
                    "membership": [
                        {id: "ObjectRelationshipTypeUSID"},
                        {id: "ChildName"},
                        {id: "PinToRevision"},
                    ],
                }],
            },
        },
        Layout: {},
        Controls: {
            ObjectRelationshipTypeUSID: {
                id: "ObjectRelationshipTypeUSID",
                enterpriseComponentAttribute: "ObjectRelationshipTypeUSID",
                placement: PLACEMENT,
                errors: [
                    {validator: "Required", value: null, errorText: "A relationship is required."},
                ],
                csp: {autoFocus: true, multiline: false, placeholder: null, label: "Relationship",
                      size: "small", disabled: false},
                ip: {},
                type: "Select",
                requiresConfirmation: false,
            },
            ChildName: {
                id: "ChildName",
                enterpriseComponentAttribute: "ChildName",
                placement: PLACEMENT,
                errors: [
                    {validator: "Required", value: null, errorText: "An object is required."},
                ],
                // A LONG LIST, so this renders as a read-only box with a chooser beside it rather
                // than a dropdown of every object in the realm.
                // REQUIRED ON THE CONTROL, because the requirement moved off this attribute when the
                // field was rebound. The obligation belongs to ChildUSID -- which the chooser fills
                // through a pick map -- and ChildName is only how it is shown, so the metadata says
                // ChildName is optional and the form printed "Optional" under a field you cannot
                // save without. The asterisk comes from the same flag.
                csp: {autoFocus: false, multiline: false, placeholder: null, label: "Object",
                      size: "small", disabled: false, required: true},
                ip: {},
                type: "Select",
                requiresConfirmation: false,
            },
            /**
             * Float or pin -- the one decision the reader makes about WHEN this link means what.
             *
             * OFF BY DEFAULT, and that default is the important part. Most links are "see also" and
             * should follow the record forward; a pin is the exception, taken deliberately, for the
             * case where the statement is about a particular revision ("raised against SOP-00018
             * REV 2") and stays about it after REV 3 is released.
             *
             * IT PINS TO THE REVISION YOU WERE LOOKING AT. The chooser lists current revisions, so
             * the one captured when you picked the object is the one on screen when you decided --
             * no separate revision picker, and no UUID anywhere near the reader.
             */
            PinToRevision: {
                id: "PinToRevision",
                enterpriseComponentAttribute: "PinToRevision",
                placement: PLACEMENT,
                errors: [],
                csp: {label: "Pin to the revision shown, instead of following the current one",
                      labelPlacement: "end"},
                ip: {},
                type: "Switch",
                requiresConfirmation: false,
            },
        },
    };

    cancel() {
        super.cancel();
        EventBus.components.emit("object-relationship-form-applet-cancel");
    };

    setAction(action) {
        super.setAction(action);

        if (action === "Save Completed") {
            EventBus.components.emit("object-relationship-form-applet-save");
        }
    };

    render() {
        return <PlatformDetailDialog applet={this}
                                     layout={this.state.Layout}
                                     controls={this.state.Controls}
                                     actions={this.state.Actions}
                                     title="Related object"
                                     enterpriseComponent={this.getEnterpriseComponent()}
                                     action={this.props.defaultAction}
                                     errorReportingMethod="RealTime"/>;
    }
}

export default ObjectRelationshipFormApplet;
